import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageSEO, HomeJsonLd } from '../components/SEO';
import { ArrowRight, Award, TrendingUp, BookOpen, ChevronRight, BarChart3, Quote, GraduationCap, Shield, Target, CheckCircle, Brain, ClipboardList, LineChart, Rocket, Compass, Map } from 'lucide-react';
import axios from 'axios';
import '@fontsource/outfit/400.css';
import '@fontsource/outfit/600.css';
import '@fontsource/outfit/700.css';
import '@fontsource/ibm-plex-sans/400.css';
import '@fontsource/ibm-plex-sans/500.css';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const HomePage = () => {
  const navigate = useNavigate();
  const [vipEmail, setVipEmail] = useState('');
  const [vipStatus, setVipStatus] = useState('');

  const handleVipSignup = async () => {
    if (!vipEmail || !vipEmail.includes('@')) { setVipStatus('Please enter a valid email.'); return; }
    try {
      await axios.post(`${API}/vip-signup`, { email: vipEmail });
      setVipStatus('Welcome to the inner circle.');
      setVipEmail('');
    } catch { setVipStatus('Something went wrong. Try again.'); }
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }} data-testid="homepage">
      <PageSEO path="/" />
      <HomeJsonLd />

      {/* ── ASK JAN AI LAUNCH BANNER ── */}
      <a href="/ask-jan" className="block w-full" style={{ background: 'linear-gradient(135deg, #0F2E58 0%, #1E3A6E 100%)' }} data-testid="ai-launch-banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-center gap-3 sm:gap-4">
          <span className="relative flex h-2.5 w-2.5 shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-400" />
          </span>
          <span className="text-white text-xs sm:text-sm font-medium">
            <strong className="font-bold">Ask Jan AI is LIVE</strong>
            <span className="hidden sm:inline"> — Your personal QRM Expert Assistant.</span>
            <span className="text-yellow-300 font-bold ml-1.5">10 free questions!</span>
          </span>
          <span className="text-xs font-bold px-3 py-1 rounded-full shrink-0 transition-transform hover:scale-105" style={{ backgroundColor: '#EAB308', color: '#0A192F' }}>
            Try it now →
          </span>
        </div>
      </a>

      {/* ── HERO — Clean Light Enterprise ── */}
      <section className="relative" style={{ backgroundColor: '#F8F9FA' }} data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-32">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 border border-yellow-300 bg-yellow-50 text-yellow-700 px-4 py-1.5 rounded-full mb-6">
              <Award className="h-4 w-4" />
              <span className="text-xs uppercase tracking-[0.15em] font-semibold">2025 Lean Lead Winner — Best QRM Practice</span>
            </div>
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold mb-5" style={{ fontFamily: "'Outfit', sans-serif", color: '#0A192F', lineHeight: '1.15' }} data-testid="hero-headline">
              Accelerate Manufacturing Lead Excellence.<br />
              <span style={{ color: '#EAB308' }}>Drive EBITDA Growth.</span>
            </h1>
            <p className="text-base sm:text-lg max-w-2xl mb-4 leading-relaxed" style={{ color: '#475569' }}>
              The Smart QRM Navigator: AI-driven strategic tools and 2025 Award-winning Quick Responsive Manufacturing (QRM) methodologies.
            </p>
            <p className="text-sm mb-8" style={{ color: '#64748B' }}>
              <GraduationCap className="h-4 w-4 inline-block mr-1.5 -mt-0.5" style={{ color: '#94A3B8' }} />
              by <span className="font-semibold" style={{ color: '#334155' }}>Ing. Jan Pauwels, MSc</span> (KU Leuven)
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button data-testid="cta-calculate-roi" onClick={() => navigate('/business-case')}
                className="inline-flex items-center justify-center gap-2 font-bold px-6 sm:px-8 py-3 sm:py-4 transition-all duration-200 rounded text-sm sm:text-base shadow-sm hover:shadow-md"
                style={{ backgroundColor: '#EAB308', color: '#0A192F' }}>
                Calculate Your ROI <ArrowRight className="h-5 w-5" />
              </button>
              <button data-testid="cta-explore-methodology" onClick={() => navigate('/qrm-education')}
                className="inline-flex items-center justify-center gap-2 font-semibold px-6 sm:px-8 py-3 sm:py-4 transition-all duration-200 rounded text-sm sm:text-base"
                style={{ border: '1.5px solid #CBD5E1', color: '#334155', backgroundColor: 'white' }}>
                Explore Methodology <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
        {/* Subtle bottom border */}
        <div className="h-px bg-gray-200" />
      </section>

      {/* ── LOGO AUTHORITY — Brand Symbolism Explanation ── */}
      <section className="py-10 sm:py-14 bg-white" data-testid="logo-explanation">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 sm:p-10" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>

            <p className="text-xs uppercase tracking-[0.2em] font-semibold mb-8" style={{ color: '#9CA3AF' }}>Logo Symbolism &amp; Mission</p>

            {/* I. Core Mission */}
            <div className="mb-8 pb-8 border-b border-gray-100">
              <h3 className="text-base sm:text-lg font-bold mb-3" style={{ fontFamily: "'Outfit', sans-serif", color: '#0F2E58' }}>I. Our Core Mission &amp; Authorization</h3>
              <p className="text-sm sm:text-base leading-relaxed" style={{ color: '#374151' }}>
                The Smart QRM Navigator logo symbolizes our mission: providing manufacturing operations with a direct, <strong style={{ color: '#EAB308' }}>'Suri-Approved' path</strong> through the complexities of high-mix, low-volume production.
              </p>
            </div>

            {/* II. Definitive Compass */}
            <div className="mb-8 pb-8 border-b border-gray-100">
              <h3 className="text-base sm:text-lg font-bold mb-3" style={{ fontFamily: "'Outfit', sans-serif", color: '#0F2E58' }}>II. The Need for a Definitive Compass</h3>
              <p className="text-sm sm:text-base leading-relaxed" style={{ color: '#374151' }}>
                Ten years ago, when I was searching for my own path through the 'tangled knot' of manufacturing complexity, I realized I lacked a true compass. This platform is the result of that search—a definitive navigator.
              </p>
            </div>

            {/* III. Visualizing Progress */}
            <div>
              <h3 className="text-base sm:text-lg font-bold mb-5" style={{ fontFamily: "'Outfit', sans-serif", color: '#0F2E58' }}>III. Visualizing Progress &amp; Profitability</h3>
              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 shrink-0 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#F1F5F9', border: '1px solid #E2E8F0' }}>
                    <Compass className="h-5 w-5" style={{ color: '#0F2E58' }} />
                  </div>
                  <div>
                    <p className="text-sm font-bold mb-1" style={{ color: '#0F2E58' }}>The 'N' (Navigator)</p>
                    <p className="text-sm leading-relaxed" style={{ color: '#4B5563' }}>
                      The central 'N' stands for 'Navigator', but its integrated nature forms a sophisticated compass needle. It visualizes <strong style={{ color: '#EAB308' }}>Purposeful Movement</strong>, cutting through confusion with surgical precision.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 shrink-0 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#FFFBEB', border: '1px solid #FDE68A' }}>
                    <TrendingUp className="h-5 w-5" style={{ color: '#D97706' }} />
                  </div>
                  <div>
                    <p className="text-sm font-bold mb-1" style={{ color: '#0F2E58' }}>The Golden Arrow (Velocity &amp; Smart Intelligence)</p>
                    <p className="text-sm leading-relaxed" style={{ color: '#4B5563' }}>
                      The upward-trending Golden Arrow represents both velocity (the 'Quick' in QRM) and progress. This is the digital intelligence ("Smart") that identifies 'system slack' and provides the predictive modeling needed to avoid demand spikes, turning optimized operations into dramatic <strong style={{ color: '#EAB308' }}>EBITDA growth</strong>.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 shrink-0 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#ECFDF5', border: '1px solid #A7F3D0' }}>
                    <Map className="h-5 w-5" style={{ color: '#059669' }} />
                  </div>
                  <div>
                    <p className="text-sm font-bold mb-1" style={{ color: '#0F2E58' }}>A Clear Roadmap</p>
                    <p className="text-sm leading-relaxed" style={{ color: '#4B5563' }}>
                      It's not just about advice; it's about providing a clear roadmap to the <strong style={{ color: '#EAB308' }}>gold standard of profitability</strong>.
                    </p>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>
      <section className="py-12 sm:py-20 bg-white" data-testid="hero-visualization">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <img
            src="/qrm-visualization.png"
            alt="The Quick Responsive Manufacturing Path: From Traditional Complexity to EBITDA Growth in 6 Steps — Time-Based Competition, MCT Decrease, Eliminating Non-Value-Added Time, WIP Reduction, Enhanced Agility, EBITDA Growth"
            className="w-full h-auto rounded-lg shadow-lg"
            data-testid="qrm-path-visual"
          />
          <div className="text-center mt-10">
            <button onClick={() => navigate('/business-case')} data-testid="viz-cta"
              className="inline-flex items-center justify-center gap-2 font-bold px-8 py-4 rounded text-base shadow-sm hover:shadow-md transition-all duration-200"
              style={{ backgroundColor: '#EAB308', color: '#0A192F' }}>
              Calculate Your ROI Potential <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </section>

      {/* ── 2025 AWARD & SURI VALIDATION ── */}
      <section className="py-12 sm:py-16 bg-white" data-testid="award-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Award block */}
            <div className="rounded-lg p-6 sm:p-8" style={{ backgroundColor: '#FFFBEB', border: '1px solid #FDE68A' }}>
              <div className="flex items-start gap-4 mb-5">
                <div className="w-12 h-12 flex items-center justify-center rounded-lg shrink-0" style={{ backgroundColor: '#FEF3C7', border: '1px solid #FDE68A' }}>
                  <Award className="h-6 w-6" style={{ color: '#D97706' }} />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold mb-1" style={{ fontFamily: "'Outfit', sans-serif", color: '#0A192F' }}>
                    2025 Best QRM Practice Award
                  </h3>
                  <p className="text-sm" style={{ color: '#92400E' }}>Lean Lead Conference, Belgium</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-3 bg-white rounded-lg border border-yellow-100">
                  <p className="text-2xl font-bold" style={{ color: '#D97706' }}>69%</p>
                  <p className="text-xs mt-1" style={{ color: '#78716C' }}>Lead Time Reduction</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg border border-yellow-100">
                  <p className="text-2xl font-bold" style={{ color: '#D97706' }}>95%</p>
                  <p className="text-xs mt-1" style={{ color: '#78716C' }}>Revenue Growth (5Y)</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg border border-yellow-100">
                  <p className="text-2xl font-bold" style={{ color: '#D97706' }}>99.5%</p>
                  <p className="text-xs mt-1" style={{ color: '#78716C' }}>Delivery Reliability</p>
                </div>
              </div>
            </div>

            {/* Suri quote block */}
            <div data-testid="suri-validation">
              <Quote className="h-6 w-6 mb-4" style={{ color: '#D4D4D8' }} />
              <blockquote className="text-base sm:text-lg leading-relaxed italic mb-5" style={{ color: '#374151' }}>
                "Another award to a company for QRM implementation! I am pleased to share that another company in Belgium has received an award for their success with QRM. Congratulations to Jan Pauwels who led these efforts..."
              </blockquote>
              <div className="flex items-center gap-3">
                <div className="w-px h-10" style={{ backgroundColor: '#EAB308' }} />
                <div>
                  <p className="font-semibold text-sm" style={{ color: '#111827' }}>Prof. Rajan Suri</p>
                  <p className="text-xs" style={{ color: '#6B7280' }}>Founder of QRM &amp; Professor Emeritus, UW-Madison</p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-5 mt-6 pt-5 border-t border-gray-100">
                <p className="text-xs uppercase tracking-wider" style={{ color: '#9CA3AF' }}>Methodology validated by</p>
                <span className="text-sm font-medium" style={{ color: '#374151' }}>BMW Germany</span>
                <span className="text-sm font-medium" style={{ color: '#374151' }}>John Deere USA</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── THREE FEATURE CARDS ── */}
      <section className="py-14 sm:py-20" style={{ backgroundColor: '#F8F9FA' }} data-testid="pillars-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-xs sm:text-sm uppercase tracking-[0.25em] font-medium mb-2" style={{ color: '#D97706' }}>The Platform</p>
            <h2 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: "'Outfit', sans-serif", color: '#0A192F' }}>
              Three Pillars of Manufacturing Intelligence
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6">
            {/* Financial Engine */}
            <div className="group cursor-pointer bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 overflow-hidden"
              onClick={() => navigate('/business-case')} data-testid="pillar-financial">
              <div className="h-1" style={{ backgroundColor: '#EAB308' }} />
              <div className="p-6 sm:p-8">
                <div className="w-10 h-10 flex items-center justify-center rounded mb-5" style={{ backgroundColor: '#FFFBEB', border: '1px solid #FDE68A' }}>
                  <BarChart3 className="h-5 w-5" style={{ color: '#D97706' }} />
                </div>
                <h3 className="text-lg sm:text-xl font-bold mb-3" style={{ fontFamily: "'Outfit', sans-serif", color: '#0A192F' }}>Financial Engine</h3>
                <p className="text-sm leading-relaxed mb-4" style={{ color: '#6B7280' }}>
                  Justify investments with a 3-year EBITDA projection. ROI forecasts, payback periods, and margin analysis — the financial dashboard your CFO expects.
                </p>
                <ul className="space-y-2 mb-5">
                  {['3-year EBITDA projection', 'Payback period analysis', 'Investment justification report'].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm" style={{ color: '#4B5563' }}>
                      <CheckCircle className="h-4 w-4 mt-0.5 shrink-0" style={{ color: '#EAB308' }} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex items-center gap-1 text-sm font-semibold group-hover:gap-2 transition-all" style={{ color: '#D97706' }}>
                  Open Calculator <ChevronRight className="h-4 w-4" />
                </div>
              </div>
            </div>

            {/* Proven Success */}
            <div className="group cursor-pointer bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 overflow-hidden"
              onClick={() => navigate('/success-cases')} data-testid="pillar-success">
              <div className="h-1" style={{ backgroundColor: '#10B981' }} />
              <div className="p-6 sm:p-8">
                <div className="w-10 h-10 flex items-center justify-center rounded mb-5" style={{ backgroundColor: '#ECFDF5', border: '1px solid #A7F3D0' }}>
                  <TrendingUp className="h-5 w-5" style={{ color: '#059669' }} />
                </div>
                <h3 className="text-lg sm:text-xl font-bold mb-3" style={{ fontFamily: "'Outfit', sans-serif", color: '#0A192F' }}>Proven Results</h3>
                <p className="text-sm leading-relaxed mb-4" style={{ color: '#6B7280' }}>
                  Case studies including our 2025 "Best QRM Practice" Award. See how manufacturers turned lead time chaos into competitive advantage.
                </p>
                <ul className="space-y-2 mb-5">
                  {['69% lead time reduction', 'From near-bankruptcy to market leader', '2025 Best QRM Practice Award'].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm" style={{ color: '#4B5563' }}>
                      <CheckCircle className="h-4 w-4 mt-0.5 shrink-0" style={{ color: '#10B981' }} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex items-center gap-1 text-sm font-semibold group-hover:gap-2 transition-all" style={{ color: '#059669' }}>
                  View Success Stories <ChevronRight className="h-4 w-4" />
                </div>
              </div>
            </div>

            {/* Expert Training */}
            <div className="group cursor-pointer bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 overflow-hidden"
              onClick={() => navigate('/qrm-education')} data-testid="pillar-methodology">
              <div className="h-1" style={{ backgroundColor: '#3B82F6' }} />
              <div className="p-6 sm:p-8">
                <div className="w-10 h-10 flex items-center justify-center rounded mb-5" style={{ backgroundColor: '#EFF6FF', border: '1px solid #BFDBFE' }}>
                  <BookOpen className="h-5 w-5" style={{ color: '#2563EB' }} />
                </div>
                <h3 className="text-lg sm:text-xl font-bold mb-3" style={{ fontFamily: "'Outfit', sans-serif", color: '#0A192F' }}>Expert Education</h3>
                <p className="text-sm leading-relaxed mb-4" style={{ color: '#6B7280' }}>
                  A comprehensive, time-based enterprise strategy for high-mix, low-volume manufacturing. QRM targets the radical reduction of Lead Times across shop floor and office operations.
                </p>
                <ul className="space-y-2 mb-5">
                  {['Alpha to Gold learning path', 'POLCA & lead time mastery', 'Rajan Suri\'s QRM principles'].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm" style={{ color: '#4B5563' }}>
                      <CheckCircle className="h-4 w-4 mt-0.5 shrink-0" style={{ color: '#3B82F6' }} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex items-center gap-1 text-sm font-semibold group-hover:gap-2 transition-all" style={{ color: '#2563EB' }}>
                  Explore Education Paths <ChevronRight className="h-4 w-4" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS — 1-2-3 Steps ── */}
      <section className="py-14 sm:py-20 bg-white" data-testid="how-it-works">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs sm:text-sm uppercase tracking-[0.25em] font-medium mb-2" style={{ color: '#9CA3AF' }}>Simple Process</p>
            <h2 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: "'Outfit', sans-serif", color: '#0A192F' }}>
              From Data Input to EBITDA Growth
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-10 relative">
            {/* Connecting line (desktop only) */}
            <div className="hidden md:block absolute top-16 left-1/6 right-1/6 h-px" style={{ backgroundColor: '#E5E7EB' }} />

            {/* Step 1 */}
            <div className="text-center relative" data-testid="step-1">
              <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full mb-5 relative z-10" style={{ backgroundColor: '#0A192F' }}>
                <ClipboardList className="h-6 w-6 text-white" />
              </div>
              <div className="inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold mb-3" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>1</div>
              <h3 className="text-lg font-bold mb-2" style={{ fontFamily: "'Outfit', sans-serif", color: '#0A192F' }}>Input Your Data</h3>
              <p className="text-sm leading-relaxed" style={{ color: '#6B7280' }}>
                Enter your current revenue, lead times, and operational parameters into the EBITDA Calculator.
              </p>
            </div>

            {/* Step 2 */}
            <div className="text-center relative" data-testid="step-2">
              <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full mb-5 relative z-10" style={{ backgroundColor: '#0A192F' }}>
                <LineChart className="h-6 w-6 text-white" />
              </div>
              <div className="inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold mb-3" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>2</div>
              <h3 className="text-lg font-bold mb-2" style={{ fontFamily: "'Outfit', sans-serif", color: '#0A192F' }}>Get 3-Year Projections</h3>
              <p className="text-sm leading-relaxed" style={{ color: '#6B7280' }}>
                Receive a detailed financial forecast: ROI timeline, payback period, and projected EBITDA margin improvement.
              </p>
            </div>

            {/* Step 3 */}
            <div className="text-center relative" data-testid="step-3">
              <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full mb-5 relative z-10" style={{ backgroundColor: '#0A192F' }}>
                <Rocket className="h-6 w-6 text-white" />
              </div>
              <div className="inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold mb-3" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>3</div>
              <h3 className="text-lg font-bold mb-2" style={{ fontFamily: "'Outfit', sans-serif", color: '#0A192F' }}>Present &amp; Execute</h3>
              <p className="text-sm leading-relaxed" style={{ color: '#6B7280' }}>
                Use your data-driven business case to secure stakeholder buy-in and launch your QRM transformation.
              </p>
            </div>
          </div>

          <div className="text-center mt-10">
            <button onClick={() => navigate('/business-case')} data-testid="how-it-works-cta"
              className="inline-flex items-center justify-center gap-2 font-bold px-8 py-3 rounded text-sm shadow-sm hover:shadow-md transition-all duration-200"
              style={{ backgroundColor: '#EAB308', color: '#0A192F' }}>
              Start Your Business Case <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>

      {/* ── MORE TOOLS ── */}
      <section className="py-14 sm:py-20" style={{ backgroundColor: '#F8F9FA' }} data-testid="tools-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-xs sm:text-sm uppercase tracking-[0.25em] font-medium mb-2" style={{ color: '#9CA3AF' }}>Also Available</p>
            <h2 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: "'Outfit', sans-serif", color: '#0A192F' }}>
              More Tools &amp; Resources
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            {[
              { icon: <Target className="h-5 w-5" />, title: 'Strategy Advisor', desc: 'AI recommendations', link: '/recommendations', color: '#059669', bg: '#ECFDF5', border: '#A7F3D0' },
              { icon: <Brain className="h-5 w-5" />, title: 'Ask Jan AI', desc: 'Coming soon', link: '/ask-jan', color: '#2563EB', bg: '#EFF6FF', border: '#BFDBFE' },
              { icon: <BookOpen className="h-5 w-5" />, title: 'QRM Vocabulary', desc: '202 expert terms', link: '/qrm-vocabulary', color: '#4B5563', bg: '#F3F4F6', border: '#E5E7EB' },
              { icon: <Award className="h-5 w-5" />, title: 'QRM Journey', desc: 'About Jan', link: '/qrm-journey', color: '#D97706', bg: '#FFFBEB', border: '#FDE68A' },
            ].map((item, i) => (
              <div key={i} onClick={() => navigate(item.link)}
                className="group cursor-pointer p-4 bg-white rounded-lg border hover:shadow-md transition-all"
                style={{ borderColor: '#E5E7EB' }}
                data-testid={`tool-card-${i}`}>
                <div className="w-8 h-8 flex items-center justify-center rounded mb-3"
                  style={{ backgroundColor: item.bg, border: `1px solid ${item.border}` }}>
                  <span style={{ color: item.color }}>{item.icon}</span>
                </div>
                <p className="font-medium text-sm" style={{ color: '#111827' }}>{item.title}</p>
                <p className="text-xs" style={{ color: '#9CA3AF' }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── VIP EARLY ACCESS ── */}
      <section className="py-12 sm:py-16 bg-white border-t border-gray-100" data-testid="vip-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-5" style={{ backgroundColor: '#FFFBEB', border: '1px solid #FDE68A', color: '#92400E' }}>
            <Shield className="h-4 w-4" />
            <span className="text-xs uppercase tracking-[0.2em] font-medium">Exclusive Access</span>
          </div>
          <h2 className="text-xl sm:text-2xl font-bold mb-3" style={{ fontFamily: "'Outfit', sans-serif", color: '#0A192F' }}>
            Early Access to Premium Features &amp; Global Launch Updates
          </h2>
          <p className="mb-6 text-sm leading-relaxed" style={{ color: '#6B7280' }}>
            Join an elite community of manufacturing leaders. Get first access to Ask Jan AI, the advanced Financial Lab modules, and exclusive strategy insights.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input type="email" value={vipEmail} onChange={(e) => setVipEmail(e.target.value)}
              placeholder="your@company.com"
              className="flex-1 px-4 py-3 bg-white text-sm rounded"
              style={{ border: '1px solid #D1D5DB', color: '#111827' }}
              data-testid="vip-email-input" />
            <button onClick={handleVipSignup} data-testid="vip-submit-btn"
              className="font-bold px-6 py-3 text-sm whitespace-nowrap rounded transition-colors hover:opacity-90"
              style={{ backgroundColor: '#EAB308', color: '#0A192F' }}>
              Join VIP List
            </button>
          </div>
          {vipStatus && <p className={`text-sm mt-3 ${vipStatus.includes('Welcome') ? 'text-emerald-600' : 'text-red-500'}`}>{vipStatus}</p>}
          <p className="text-xs mt-4" style={{ color: '#9CA3AF' }}>No spam. Only launch updates and premium feature access.</p>
        </div>
      </section>

      {/* ── CTA FOOTER ── */}
      <section className="py-12 sm:py-16" style={{ backgroundColor: '#F8F9FA' }} data-testid="cta-footer">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-3" style={{ fontFamily: "'Outfit', sans-serif", color: '#0A192F' }}>
            Ready to Quantify Your QRM Impact?
          </h2>
          <p className="mb-6 text-sm sm:text-base" style={{ color: '#6B7280' }}>
            Join manufacturing leaders who make data-driven QRM decisions.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button data-testid="cta-footer-roi" onClick={() => navigate('/business-case')}
              className="inline-flex items-center justify-center gap-2 font-bold px-6 sm:px-8 py-3 sm:py-4 rounded text-sm sm:text-base shadow-sm hover:shadow-md transition-all"
              style={{ backgroundColor: '#EAB308', color: '#0A192F' }}>
              Calculate Your 3-Year ROI <ArrowRight className="h-5 w-5" />
            </button>
            <button data-testid="cta-footer-contact" onClick={() => navigate('/contact')}
              className="inline-flex items-center justify-center gap-2 font-semibold px-6 sm:px-8 py-3 sm:py-4 rounded text-sm sm:text-base transition-colors"
              style={{ border: '1.5px solid #D1D5DB', color: '#374151', backgroundColor: 'white' }}>
              Contact Jan Pauwels
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
